import React, { useCallback, memo } from "react";
import { Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import moment from "moment";
import Project from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Project.svg";
import Role from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Role.svg";
import ClientIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/client.svg";
import LocationIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Location.svg";
import TechnogyIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Technology.svg";
import TeamSizeIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Team_Size.svg";
import FromDateIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/From_Date.svg";
import ToDateIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/To_Date.svg";

// Define consistent column widths for both tables
const COLUMN_WIDTHS = {
  Project:"90px",
  role: "110px",
  client: "120px", // Slightly wider for client names
  location: "90px",
  technology: "70px",
  teamSize: "70px",
  fromDate: "80px",
  toDate: "80px"
};

const ProjectTable = memo(({ projects, emptyMessage }) => {
  const renderTooltip = useCallback((text) => <Tooltip id="tooltip">{text}</Tooltip>, []);

  const formatDate = useCallback((date) => {
    return date ? moment(date).format("DD-MMM-YYYY") : "";
  }, []);

  const TableHeader = memo(({ icon, label, width }) => (
    <th style={{ width }}>
      <div className="d-flex flex-column align-items-center fixed-width-column">
        <div>
          <img src={icon} alt={label.toLowerCase()} className="tech-icon" />
        </div>
        <div style={{ fontSize: "12px" }}>{label}</div>
      </div>
    </th>
  ));

  const TableCell = memo(({ value, isCenter = false, width }) => (
    <td
      className={`fixed-width-column ${isCenter ? "text-center" : ""}`}
      style={{ width }}
    >
      <OverlayTrigger placement="top" overlay={renderTooltip(value)}>
        <span className="d-inline-block text-truncate" style={{ maxWidth: "100%" }}>
          {value || ""}
        </span>
      </OverlayTrigger>
    </td>
  ));

  return (
    <div className="project-table-container">
      <style>
        {`
          .project-table-container {
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          .fixed-project-table {
            table-layout: fixed;
            width: 100%;
            min-width: 900px; /* Minimum width to ensure columns don't collapse */
          }
          
          .fixed-width-column {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          @media (min-width: 992px) {
            .fixed-project-table {
              min-width: 100%;
            }
          }
          
          .tech-icon {
            width: 20px;
            height: 20px;
          }
          
          .forum-tab-btn {
            padding: 0.5rem 1rem;
            font-size: 0.875rem;
          }
        `}
      </style>
      
      <Table striped bordered hover className="fixed-project-table">
        <colgroup>
         <col style={{ width: COLUMN_WIDTHS.Project }} />
         <col style={{ width: COLUMN_WIDTHS.client }} />
          <col style={{ width: COLUMN_WIDTHS.role }} />
          <col style={{ width: COLUMN_WIDTHS.location }} />
          <col style={{ width: COLUMN_WIDTHS.technology }} />
          <col style={{ width: COLUMN_WIDTHS.teamSize }} />
          <col style={{ width: COLUMN_WIDTHS.fromDate }} />
          <col style={{ width: COLUMN_WIDTHS.toDate }} />
        </colgroup>
        <thead className="text-center">
          <tr>
            <TableHeader icon={Project} label="Proj." width={COLUMN_WIDTHS.Project} />
            <TableHeader icon={ClientIcon} label="Client" width={COLUMN_WIDTHS.client} />
            <TableHeader icon={Role} label="Role" width={COLUMN_WIDTHS.role} />
            <TableHeader icon={LocationIcon} label="Location" width={COLUMN_WIDTHS.location} />
            <TableHeader icon={TechnogyIcon} label="Technology" width={COLUMN_WIDTHS.technology} />
            <TableHeader icon={TeamSizeIcon} label="Team Size" width={COLUMN_WIDTHS.teamSize} />
            <TableHeader icon={FromDateIcon} label="From Date" width={COLUMN_WIDTHS.fromDate} />
            <TableHeader icon={ToDateIcon} label="To Date" width={COLUMN_WIDTHS.toDate} />
          </tr>
        </thead>
        <tbody>
          {projects?.length > 0 ? (
            projects.map((project, index) => (
              <tr key={index}>
                <TableCell value={project.Project} width={COLUMN_WIDTHS.Project} />
                <TableCell value={project.Client} width={COLUMN_WIDTHS.client} />
                <TableCell value={project.Role} width={COLUMN_WIDTHS.role} />
                <TableCell value={project.Location} width={COLUMN_WIDTHS.location} />
                <TableCell value={project.Technology} width={COLUMN_WIDTHS.technology} />
                <TableCell 
                  value={project.TeamSize} 
                  isCenter 
                  width={COLUMN_WIDTHS.teamSize} 
                />
                <TableCell 
                  value={formatDate(project.FromDate || project.StartDate)} 
                  isCenter 
                  width={COLUMN_WIDTHS.fromDate} 
                />
                <TableCell 
                  value={formatDate(project.ActualEndDate || project.EndDate)} 
                  isCenter 
                  width={COLUMN_WIDTHS.toDate} 
                />
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="8" className="text-center">
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
});

const ProjectTabs = memo(({ currentProjectsData, previousProjectsData }) => {
  return (
    <div className="container-fluid p-0 mt-2">
      <div className="p-0 forum-tab">
        <ul className="nav feed-nav-tabs overflow multi-tabs" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              className="nav-link forum-tab-btn active"
              id="Current-tab"
              data-bs-toggle="tab"
              data-bs-target="#Current"
              type="button"
              role="tab"
              aria-controls="feed"
              aria-selected="true"
            >
              <strong>Current Projects</strong>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className="nav-link forum-tab-btn"
              id="Previous-tab"
              data-bs-toggle="tab"
              data-bs-target="#Previous"
              type="button"
              role="tab"
              aria-controls="Previous"
              aria-selected="false"
            >
              <strong>Previous Projects</strong>
            </button>
          </li>
        </ul>
      </div>
      <div className="project_sect">
        <div className="tab-content" id="myTabContent">
          <div
            className="tab-pane fade show active"
            id="Current"
            role="tabpanel"
            aria-labelledby="Current-tab"
          >
            <ProjectTable
              projects={currentProjectsData}
              emptyMessage="No Current Projects Available"
            />
          </div>
          <div
            className="tab-pane fade"
            id="Previous"
            role="tabpanel"
            aria-labelledby="Previous-tab"
          >
            <ProjectTable
              projects={previousProjectsData}
              emptyMessage="No Previous Projects Available"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

const ProjectComponent = ({
  currentProjectsData = [],
  previousProjectsData = [],
}) => {
  return (
    <ProjectTabs
      currentProjectsData={currentProjectsData}
      previousProjectsData={previousProjectsData}
    />
  );
};

export default memo(ProjectComponent);